import { createContext, useContext } from 'react';
import {
  AutoCombinationKey,
  AutoCorrelationReport,
  AutoCorrelationScopeMode,
  CorrelationEndpointSource,
  SelectedFziFunctionsOptions,
  XAxisVariable
} from '../../@types/projects/types';

/**
 * Light context module — intentionally free of API/provider imports so leaf
 * consumers (e.g. the "Auto" badge in chartCorrelation) can read the context
 * without dragging the axios/auth stack into their module graph.
 */

export type AutoCorrelationStep = 'scope' | 'run' | 'review';

export type AutoCorrelationFamily =
  | 'liao'
  | 'lambda'
  | 'water_oil'
  | 'gas_oil';

export type ScopeSampleUids = Record<AutoCorrelationFamily, string[]>;

export const DEFAULT_GRAD_DIFF = 0.075;

export interface AutoCorrelationContextProps {
  /** Whether the Auto Correlation wizard modal is open. */
  isOpen: boolean;

  /** Current wizard step. */
  step: AutoCorrelationStep;

  /** How the sample scope was resolved (echoed to the backend for audit). */
  scopeMode: AutoCorrelationScopeMode;

  /** Resolved per-family sample uids sent on run. */
  scopeSampleUids: ScopeSampleUids;

  /** X-axis variable for the automatic fits ('fzi' | 'rqi'). */
  xVariable: XAxisVariable;

  /** Gradient difference used for height curves (default 0.075). */
  gradDiff: number;

  /** Kr endpoints used by both capillary-pressure methodologies. */
  endpointSource: CorrelationEndpointSource;

  /** The last run/restored report (also quietly restored per project). */
  report: AutoCorrelationReport | null;

  /** True while run/apply/restore requests are in flight. */
  loading: boolean;

  /** Last request error message, if any. */
  error: string | null;

  /** Per-parameter user overrides of the recommended tendency type. */
  overrides: Record<string, SelectedFziFunctionsOptions>;

  /** Which combination set tab is active in the review step. */
  activeCombinationKey: AutoCombinationKey;

  /** The selection that apply() will persist (recommended + overrides). */
  stagedSelection: Record<string, SelectedFziFunctionsOptions>;

  openModal: () => void;
  closeModal: () => void;
  setStep: (step: AutoCorrelationStep) => void;
  setScope: (mode: AutoCorrelationScopeMode, uids: ScopeSampleUids) => void;
  setXVariable: (value: XAxisVariable) => void;
  setGradDiff: (value: number) => void;
  setEndpointSource: (value: CorrelationEndpointSource) => void;
  setOverride: (
    parameter: string,
    type: SelectedFziFunctionsOptions | null
  ) => void;
  setActiveCombinationKey: (key: AutoCombinationKey) => void;

  /** Runs the auto correlation for the current scope (no project mutation). */
  run: () => Promise<void>;

  /** Applies the staged selection and updates the selected project. */
  apply: () => Promise<void>;

  /** Restores the last persisted report into the review step. */
  restoreLatest: () => Promise<void>;
}

export const AutoCorrelationContext = createContext<
  AutoCorrelationContextProps | undefined
>(undefined);

export const useAutoCorrelation = (): AutoCorrelationContextProps => {
  const context = useContext(AutoCorrelationContext);
  if (!context) {
    throw new Error(
      'useAutoCorrelation must be used within an AutoCorrelationProvider'
    );
  }
  return context;
};

/**
 * Non-throwing variant for opt-in consumers (e.g. the "Auto" badge in the
 * correlation charts, which also render in trees without this provider).
 */
export const useAutoCorrelationOptional = ():
  | AutoCorrelationContextProps
  | undefined => useContext(AutoCorrelationContext);
